import { IAppContext } from '../context/DefaultAppState';
import * as _ from 'lodash';
import { DirectoryListType } from '../components/directory-list/DirectoryList';
import IGenericResult from '../interfaces/IGenericResult';
import ResultsPage from '../pages/results';
import IDistance from '../interfaces/IDistance';
import { AppContext } from '../context/AppContext';
import axios from 'axios';
import { useContext } from 'react';
import ISearch from '../interfaces/ISearch';


/**
 * @param {Boolean} shouldShowMore Whether or not more than the minimum number of specialty groups should display.
 * @param {IAppContext} appContext The provided application context.
 * @description Determines what specialty groups to display based on what directory list type is currently selected.
 * @returns {String[]} A list of categories (specialty groups).
 */
export const getCategoriesToMap = (shouldShowMore: boolean, appContext: IAppContext, isNotDropDown?: boolean): string[] => {
  let specialties: string[];

  switch (appContext.directoryListType) {
    
    case DirectoryListType.doctor:        
    specialties = _.sortBy(
      _.uniqBy(appContext.doctorSpecialties, 'specialtyGroup')
        .map((ds) => {
          if (!ds.specialtyGroup) {//console.warn('Null or undefined specialtyGroup found in doctorSpecialties:',ds);
            return '';
          }
          return _.startCase(ds.specialtyGroup.toLowerCase());
        }),
      (x: any) => x.toLowerCase()
  );
      break;

    
      case DirectoryListType.pharmacy:
        specialties = _.sortBy(
          _.uniq(appContext.pharmacySpecialties)
            .map((ps) => {
              if (!ps) {
                return ''; 
              }
              return _.startCase(ps.toLowerCase());
            }),
          (x: any) => x.toLowerCase()
        );
        break;
        case DirectoryListType.facility:
          specialties = _.sortBy(
            _.uniqBy(appContext.facilitySpecialties, 'specialtyGroup').map((fs) => {
                if (!fs.specialtyGroup) {
                  return ''; 
                }
                return _.startCase(fs.specialtyGroup.toLowerCase());
              }),
            (x: any) => x.toLowerCase()
          );
          break;
        
      case DirectoryListType.all:
        const doctorSpecialties = appContext.doctorSpecialties || [];
        const pharmacySpecialties = appContext.pharmacySpecialties ? appContext.pharmacySpecialties.map(ps => ({ specialtyGroup: ps })) : [];
        const facilitySpecialties = appContext.facilitySpecialties || [];
      
        specialties = _.sortBy(
          _.uniqBy(
            [...doctorSpecialties, ...facilitySpecialties, ...pharmacySpecialties], 
            'specialtyGroup'
          ).map((s) => {
            if (!s.specialtyGroup) {
              //console.warn('Skipping entry (null values are not allowed):', s.specialtyGroup);
              return ''; 
            }
            return _.startCase(s.specialtyGroup.toLowerCase()); // Transform the specialtyGroup to proper case
          }),
          (x: any) => x.toLowerCase()
        );break;
    default:
      specialties = [];
      break;
  }

  if (specialties.length && !isNotDropDown) {
    specialties.unshift('All Specialties');
  }

  const numToMap = shouldShowMore ? specialties.length : 9;
  return _.take(specialties, numToMap);
};

/**
 * @param {IAppContext} appContext - The provided app context.
 * @param {String} state - The state name selected from the control.
 * @description Sets the disclaimer url in the header according to the selected plan state.
 */
export const UpdateDisclaimerUrl = (appContext: IAppContext, state: string) => {
  const plan = appContext.plansData.find(p => p.state === state);

  if (plan) {
    appContext.updateDisclaimerUrl(plan.ipdDisclaimerLink);
    appContext.updatePlanCode(plan.contractId);
    appContext.updatePlan(plan);
  }
};

/**
 * @param toUrl - The Url to navigate to.
 * @returns {String} path
 * @description Returns path to state selection, if no state was selected in the drop down.
 */
export const RouteWithState = (toUrl: string, appContext: IAppContext, specialty?: string) => {
  if(specialty) {
    appContext.updateSelectedSpecialty(specialty);
  }
  //return appContext.planState ? toUrl : `/state-selection`; //original
  return toUrl;
};

export const renderCmsIdNumber = (appContext: IAppContext) => {
  if (appContext.plan && appContext.planState) {
    const planCode = appContext.plan.contractId;
    appContext.updatePlanCode(planCode);
    return appContext.planCode;
  }
  return '';
};

/**
 * @param rawResults - Unmapped Facility, Pharmacy, or Doctor Results
 * @returns {IGenericResult[]} genericResults
 */
export const mapToGenericResults = (rawResults: any[]): IGenericResult[] => {
  let genericResults: IGenericResult[] = [];
  //console.log(rawResults)

  //For testing
  const includedZIP = true;
  //approximateDistance: includedZIP ? i + 1 : null

  //
  rawResults.map((result, i) => {
    if (result.isDoctor) {
      genericResults.push({
        recordId: result.recordId,
        name: `${result.firstName} ${result.lastName}${result.credentials ? ', ' + result.credentials : ''}`,
        locationName: result.practices,
        specialty: result.specialty,
        specialtyGroup: result.specialtyGroup,
        addressLine1: result.addressLine1,
        addressLine2: result.addressLine2,
        zipCode: result.zipCode,
        city: result.city,
        state: result.state,
        phone: result.phone,
        fax: result.fax || 'N/A',
        acceptingNewPatients: result.acceptingNewPatients,
        providerNPI: result.providerNPI,
        boardCertification: result.boardCertification,
        practice: result.practices,
        language: result.language,
        approximateDistance: result.approximateDistance,
		serviceMode: result.serviceMode
      });
    } else if (result.isPharmacy) {
      genericResults.push({
        recordId: result.recordId,
        name: result.pharmacyNameDir,
        locationName: result.pharmacyNameDir,
        specialty: result.pharmacyType,
        specialtyGroup: result.specialtyGroup,
        addressLine1: result.addressLine1,
        addressLine2: result.addressLine2,
        zipCode: result.zipCode,
        city: result.city,
        state: result.state,
        fax: result.fax || 'N/A',
        phone: result.phone,
        providerNPI: result.providerNPI,
        approximateDistance: result.approximateDistance,
		serviceMode: result.serviceMode
      });
    } else { // Facility
      genericResults.push({
        recordId: result.recordId,
        name: result.name,
        locationName: result.name,
        specialty: result.specialty,
        specialtyGroup: result.specialtyGroup,
        addressLine1: result.addressLine1,
        addressLine2: result.addressLine2,
        zipCode: result.zipCode,
        city: result.city,
        state: result.state,
        fax: result.fax || 'N/A',
        phone: result.phone,
        providerNPI: result.providerNPI,
        approximateDistance: result.approximateDistance,
		serviceMode: result.serviceMode
      });
    }
  });
  
  return genericResults;
};


/**
 *
 */
export function getCurrentPosition(options = {}) {
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(resolve, reject, options);
  });
};

/**
 *
 */
export const getCurrentPositionError = async (err: any) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const appContext = useContext(AppContext);
  const formattedAddress = appContext.planState;
  const geocode = await axios.get(`https://maps.googleapis.com/maps/api/geocode/json?address=${formattedAddress}&key=${process.env.REACT_APP_MAPS_API_KEY}`);
  const locationToMark: { lat: number; lng: number } = geocode.data.results[0]?.geometry?.location;
  return 
};

/**
 *
 */
 export function distanceOptions(): IDistance[] {
  const distanceObject: IDistance[] = [
    {
      name: 'Select Distance',
      distance: -1
    },
    {
      name: '1 mile',
      distance: 1
    },
    {
      name: '3 miles',
      distance: 3
    },
    {
      name: '5 miles',
      distance: 5
    },
    {
      name: '10 miles',
      distance: 10
    },
    {
      name: '15 miles',
      distance: 15
    },
    {
      name: '20 miles',
      distance: 20
    },
    {
      name: '30 miles',
      distance: 30
    },
    {
      name: '40 miles',
      distance: 40
    },
    {
      name: '50 miles',
      distance: 50
    },
    {
      name: '75 miles',
      distance: 75
    },
    {
      name: '100 miles',
      distance: 100
    }
  ];
  return distanceObject
};

export const checkValidZip = ( zipCode: string ) => {
  console.log('validZip? ', zipCode)
  const regex = /^[0-9]{5}(?:-[0-9]{4})?$/;
  let isValid = regex.test(zipCode);
  //console.log('checkValidZip: ',zipCode, isValid)
  return isValid;
};

export const isEmptyZip = (zipCode: string) => {
  //console.log('checkIfRequired: ', zipCode)
  if (zipCode.toUpperCase() === 'UNDEFINED') {
    return true
  }
  if (zipCode === '' || !zipCode) {
    return true;
  }
  return false
};

export const evaluateIsDisabled = (newSearch: ISearch, location: string | undefined | null, distance: number) => {
  console.log('search: ', newSearch)
  console.log('eval location: ', location ? location : undefined)
  
  const case1 = { required: true, isValidZIP: true }; 
  const case2 = { required: true, isValidZIP: false };
  const case3 = { required: false, isValidZIP: false }; //and isEmptyZip returns 
  const case4 = { required: false , isValidZIP: true };

  const tempSearch: ISearch = {
    required: newSearch.required, 
    isValidZIP: newSearch.isValidZIP 
  }

  const jsonSearch = JSON.stringify(tempSearch)
  const jsonCase1 = JSON.stringify(case1)
  const jsonCase2 = JSON.stringify(case2)
  const jsonCase3 = JSON.stringify(case3)
  const jsonCase4 = JSON.stringify(case4)

  if (jsonSearch === jsonCase1) {
    return false
  } else if (jsonSearch === jsonCase2) {
    return true
  } else if (jsonSearch === jsonCase3) {
    //console.log(location === '' || (location ? isEmptyZip(location) : false))
    if (!location || location === '' || (location && isEmptyZip(location))) {
      console.log('3alt', location, location && isEmptyZip(location))
      return false
    }
    console.log('3d', location, isEmptyZip(location))
    return true
  } else if (jsonSearch === jsonCase4) {
    return false
  } else {
    return false
  }

};